import { motion } from "framer-motion"
import { DollarSign, GraduationCap, Heart, Briefcase, Users, Wifi, Globe, Baby, BookOpen, Zap, Languages, TrendingUp, ArrowRight } from "lucide-react"
import Layout from "@/components/Layout"
import SectionHeader from "@/components/SectionHeader"
import { IMAGES } from "@/assets/images"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import { Button } from "@/components/ui/button"
import { ROUTE_PATHS } from "@/lib/index"
import { Link } from "react-router-dom"

const problems = [
  {
    icon: DollarSign,
    title: "Financial Stress",
    description: "Families struggle with budgeting, debt, and making ends meet",
  },
  {
    icon: GraduationCap,
    title: "Education Gaps",
    description: "Limited access to quality tutoring and learning support",
  },
  {
    icon: Heart,
    title: "Health Confusion",
    description: "Difficulty understanding medical information and wellness guidance",
  },
  {
    icon: Briefcase,
    title: "Unemployment",
    description: "Lack of career guidance, CV help, and job search support",
  },
  {
    icon: Users,
    title: "Family Pressure",
    description: "Balancing work, parenting, and household responsibilities",
  },
  {
    icon: Wifi,
    title: "Digital Exclusion",
    description: "Complex technology creates barriers to accessing support",
  },
]

const differentiators = [
  {
    icon: Globe,
    title: "Built for Africa",
    description: "Localised for African languages, laws, services, and culture",
  },
  {
    icon: Baby,
    title: "Built for Families",
    description: "Support for up to 5 family members across all life stages",
  },
  {
    icon: BookOpen,
    title: "No Digital Literacy Required",
    description: "Simple messaging interface anyone can use",
  },
  {
    icon: Zap,
    title: "Soft Introduction to Intelligent Tech",
    description: "Practical AI that builds trust through everyday usefulness",
  },
  {
    icon: Languages,
    title: "Built for Localisation",
    description: "Adaptable to any African country, language, or context",
  },
  {
    icon: TrendingUp,
    title: "Built for Scale",
    description: "Platform designed to reach millions of families",
  },
]

export default function About() {
  return (
    <Layout>
      <div className="relative overflow-hidden">
        <section className="relative min-h-[60vh] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.AFRICA_TECH_9}
              alt="African technology"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-background" />
          </div>

          <motion.div
            className="relative z-10 container mx-auto px-4 py-24 text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              Building practical support for everyday African life
            </h1>
          </motion.div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  Improve lives across Africa by making trusted support easier to access.
                </h2>
              </motion.div>

              <motion.div
                className="grid sm:grid-cols-2 gap-6"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
              >
                {problems.map((problem) => {
                  const Icon = problem.icon
                  return (
                    <motion.div
                      key={problem.title}
                      variants={staggerItem}
                      className="bg-card rounded-2xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {problem.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {problem.description}
                      </p>
                    </motion.div>
                  )
                })}
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Commercial Model"
              headline="Sustainable support through business deployment"
              subheadline="Businesses deploy Sammo to their customers, members, or employees — creating value while funding wider access"
              align="center"
              className="mb-16"
            />

            <motion.div
              className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div
                variants={staggerItem}
                className="relative bg-card rounded-2xl p-8 border border-border shadow-sm"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                  <Briefcase className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Businesses Deploy
                </h3>
                <p className="text-muted-foreground text-center text-sm">
                  Companies offer Sammo to customers, members, or employees as a value-added benefit
                </p>
                <div className="absolute -right-4 top-1/2 -translate-y-1/2 hidden md:block">
                  <ArrowRight className="w-8 h-8 text-accent" />
                </div>
              </motion.div>

              <motion.div
                variants={staggerItem}
                className="relative bg-card rounded-2xl p-8 border border-border shadow-sm"
              >
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 mx-auto">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Families Benefit
                </h3>
                <p className="text-muted-foreground text-center text-sm">
                  Households receive practical support across money, education, health, and daily life
                </p>
                <div className="absolute -right-4 top-1/2 -translate-y-1/2 hidden md:block">
                  <ArrowRight className="w-8 h-8 text-accent" />
                </div>
              </motion.div>

              <motion.div
                variants={staggerItem}
                className="bg-card rounded-2xl p-8 border border-border shadow-sm"
              >
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 mx-auto">
                  <Globe className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Funds Wider Access
                </h3>
                <p className="text-muted-foreground text-center text-sm">
                  Revenue enables platform development and expansion to underserved communities
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Why Different"
              headline="Built specifically for African families"
              subheadline="Not adapted from elsewhere — designed from the ground up for African contexts"
              align="center"
              className="mb-16"
            />

            <motion.div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {differentiators.map((item) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.title}
                    variants={staggerItem}
                    className="bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-md transition-all hover:scale-[1.02]"
                  >
                    <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {item.description}
                    </p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent/90 to-primary" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
          </div>

          <motion.div
            className="relative z-10 container mx-auto px-4 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Reaching 100 million families with practical support
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Our vision is to make trusted, practical support accessible to every African family
            </p>
          </motion.div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="mb-12">
                <img
                  src={IMAGES.FAMILY_TECH_4}
                  alt="Sammo team"
                  className="w-full h-80 object-cover rounded-3xl shadow-lg"
                />
              </div>

              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Join us in building everyday support at scale
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Whether you're a business looking to add value, a partner wanting to expand into new markets, or someone who shares our mission — let's talk.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="text-lg px-8 py-6 rounded-2xl"
                >
                  <Link to={ROUTE_PATHS.FOR_BUSINESS}>
                    See Sammo for Your Business
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-6 rounded-2xl"
                >
                  <Link to={ROUTE_PATHS.CONTACT}>
                    Get in Touch
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  )
}